import React, { useState } from 'react';
import { Copy, Check, Plus, Save } from 'lucide-react';
import AssessmentItem from './AssessmentItem';
import NotesPanel from '../Layout/NotesPanel';
import { formatDomainName } from '../../utils/formatters';
import { copyToClipboard } from '../../utils/clipboard';
import { formatResultsForCopy, formatItemForCopy } from '../../utils/formatters';
import { DOMAIN_ORDER } from '../../config/constants';

const AssessmentResults = ({ 
  results, 
  notes,
  editingItem,
  editedScore,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onRevert,
  onNewAssessment,
  onSaveToCase,
  onScoreChange,
  currentCase
}) => {
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  const handleCopyAll = () => {
    const text = formatResultsForCopy(results);
    copyToClipboard(text);
    setCopyFeedback('copied');
    setTimeout(() => setCopyFeedback(''), 2000);
  };

  const handleCopyItem = (item) => {
    const text = formatItemForCopy(item);
    copyToClipboard(text);
    setCopyFeedback('copied');
    setTimeout(() => setCopyFeedback(''), 2000);
  };

  const renderCategory = (prefix, title) => {
    const items = Object.entries(results.results).filter(([key]) => key.startsWith(prefix));
    if (items.length === 0) return null;

    // Sort items according to DOMAIN_ORDER
    const sortedItems = items.sort((a, b) => {
      const indexA = DOMAIN_ORDER.indexOf(a[0]);
      const indexB = DOMAIN_ORDER.indexOf(b[0]);
      // If not found in order array, put at end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return (
      <div className="mb-6">
        <h5 className="text-lg font-semibold text-gray-800 mb-3">{title}</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sortedItems.map(([key, item]) => {
            const domainName = formatDomainName(key, prefix);
            const isEditing = editingItem === `${prefix}-${key}`;
            
            return (
              <AssessmentItem
                key={key}
                item={{ ...item, name: domainName, id: key }}
                category={prefix}
                isEditing={isEditing}
                editedScore={editedScore}
                onStartEdit={onStartEdit}
                onSaveEdit={onSaveEdit}
                onCancelEdit={onCancelEdit}
                onRevert={onRevert}
                onCopy={handleCopyItem}
                onScoreChange={onScoreChange}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Left Panel - Notes (Collapsible) */}
      <NotesPanel 
        notes={notes} 
        isExpanded={notesExpanded} 
        onToggle={() => setNotesExpanded(!notesExpanded)} 
      />

      {/* Right Panel - Results */}
      <div className={`overflow-y-auto transition-all duration-300 ${notesExpanded ? 'w-3/5' : 'flex-1'}`}>
        <div className="sticky top-0 bg-white border-b border-gray-300 p-4 z-10">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleCopyAll}
              className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 flex items-center gap-2"
            >
              {copyFeedback === 'copied' ? (
                <>
                  <Check size={20} /> Copied!
                </>
              ) : (
                <>
                  <Copy size={20} /> Copy All
                </>
              )}
            </button>
            <button
              onClick={onSaveToCase}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 flex items-center gap-2"
              title={currentCase ? `Save assessment to ${currentCase.name}` : 'Save to case record'}
            >
              <Save size={20} /> Save to Case
            </button>
            <button
              onClick={onNewAssessment}
              className="bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 border-2 border-teal-600 flex items-center gap-2"
            >
              <Plus size={20} /> New Assessment
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* <h3 className="text-2xl font-bold text-gray-900 mb-6">Assessment Results</h3> */}

          <div className="mb-8">
            <h4 className="text-xl font-bold text-teal-700 mb-4 pb-2 border-b-2 border-teal-700">
              FIM/FAM Assessment Results (36 Domains)
            </h4>

            {renderCategory('selfcare_', 'Self Care')}
            {renderCategory('bladder_', 'Bladder Management')}
            {renderCategory('bowel_', 'Bowel Management')}
            {renderCategory('mobility_', 'Mobility')}
            {renderCategory('locomotion_', 'Locomotion')}
            {renderCategory('communication_', 'Communication')}
            {renderCategory('psychosocial_', 'Psychosocial')}
            {renderCategory('cognition_', 'Cognition')}
            {renderCategory('adl_', 'Activities of Daily Living')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentResults;

